import React, { useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, FormFeedback, Row, Col } from 'reactstrap'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { gql, useMutation, useQuery } from '@apollo/client'
import { SubrogationClaimSchema } from './IncidentModalsSchema'
import Select from 'react-select'

const CREATE_SUBROGATION_CLAIM = gql`
  mutation CreateSubrogationClaim($input: CreateSubrogationClaimMutationInput!) {
    createSubrogationClaim(input: $input) {
      ok
      errors { field messages }
    }
  }
`

const SUBROGATION_STATUS_CHOICES = gql`
  query SubrogationStatusChoices {
    subrogationStatusChoices: __type(name: "IncidentsSubrogationClaimStatusChoices") {
      enumValues { name description }
    }
  }
`

const ALL_TPAS = gql` query AllTpas($incidentId: ID) {
    allTpas(incidentId: $incidentId, claimType: "SUBROGATION") { 
        edges { 
            node { 
                id 
                name
            } 
        }
    }
  }
`
const ALL_INCIDENT_PARTIES = gql`
  query AllIncidentParties($incidentId: ID!) {
    allIncidentParties(incidentId: $incidentId) {
      edges {
        node {
          id
          partyType
          partyName
        }
      }
    }
  }
`

const AddSubrogationClaimModal = ({ isOpen, toggle, incidentId, onSuccess }) => {
    const [error, setError] = useState(null)

    const { data: statusChoicesData } = useQuery(SUBROGATION_STATUS_CHOICES)
    const { data: tpaData } = useQuery(ALL_TPAS, { variables: { incidentId }, skip: !incidentId })
    const { data: partiesData } = useQuery(ALL_INCIDENT_PARTIES, { variables: { incidentId }, skip: !incidentId })
    const [createClaim, { loading }] = useMutation(CREATE_SUBROGATION_CLAIM)

    const statusOptions = statusChoicesData?.subrogationStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []

    const tpaOptions = tpaData?.allTpas?.edges?.map(edge => ({
        label: edge.node.name,
        value: edge.node.id
    })) || []

    const parties = partiesData?.allIncidentParties?.edges?.map(edge => edge.node) || []

    const { control, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: yupResolver(SubrogationClaimSchema),
        defaultValues: { incidentId },
        mode: "onSubmit",
    })

    const closeAndReset = () => {
        reset()
        toggle()
    }

    const onSubmit = async (values) => {
        const input = { ...values }
        try {
            const { data } = await createClaim({ variables: { input } })
            if (data?.createSubrogationClaim?.ok) {
                onSuccess && onSuccess()
                closeAndReset()
            }
            if (data?.createSubrogationClaim?.errors?.length) {
                setError(data.createSubrogationClaim.errors[0].messages[0])
            }
        } catch (e) {
            setError(e.message)
        }
    }

    return (
        <Modal isOpen={isOpen} toggle={closeAndReset} className='subrogation-claim-modal'>
            <ModalHeader>Add Subrogation Claim</ModalHeader>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                    <Controller name="incidentId" control={control} render={({ field }) => <Input type="hidden" {...field} />} />
                    <Row>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="partyId">Party <span style={{ color: 'red' }}>*</span></Label>
                                <Controller
                                    name="partyId"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            inputId="partyId"
                                            options={parties.map(p => ({ value: p.id, label: p.partyName }))}
                                            value={parties.map(p => ({ value: p.id, label: p.partyName })).find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            isClearable
                                            placeholder="Select the party involved"
                                        />
                                    )}
                                />
                                <FormFeedback style={{ display: errors.partyId ? 'block' : 'none' }}>{errors.partyId?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="tpaId">TPA (Third Party Administrator) <span style={{ color: 'red' }}>*</span></Label>
                                <Controller
                                    name="tpaId"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            inputId="tpaId"
                                            options={tpaOptions}
                                            value={tpaOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            isClearable
                                            placeholder="Select TPA"
                                        />
                                    )}
                                />
                                <FormFeedback style={{ display: errors.tpaId ? 'block' : 'none' }}>{errors.tpaId?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="status">Status</Label>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            inputId="status"
                                            options={statusOptions}
                                            value={statusOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            isClearable
                                            placeholder="Select claim status"
                                        />
                                    )}
                                />
                                <FormFeedback style={{ display: errors.status ? 'block' : 'none' }}>{errors.status?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="tpaSubmissionDate">TPA Submission Date</Label>
                                <Controller name="tpaSubmissionDate" control={control} render={({ field }) => (
                                    <Input id="tpaSubmissionDate" type="date" {...field} invalid={!!errors.tpaSubmissionDate} placeholder="Select TPA submission date (required)" />
                                )} />
                                <FormFeedback>{errors.tpaSubmissionDate?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="tpaAcknowledgmentDate">TPA Acknowledgment Date</Label>
                                <Controller name="tpaAcknowledgmentDate" control={control} render={({ field }) => (
                                    <Input id="tpaAcknowledgmentDate" type="date" {...field} invalid={!!errors.tpaAcknowledgmentDate} placeholder="Select TPA acknowledgment date (required)" />
                                )} />
                                <FormFeedback>{errors.tpaAcknowledgmentDate?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="tpaFee">TPA Fee</Label>
                                <Controller name="tpaFee" control={control} render={({ field }) => (
                                    <Input id="tpaFee" type="number" {...field} invalid={!!errors.tpaFee} placeholder="Enter TPA fee (if any)" />
                                )} />
                                <FormFeedback>{errors.tpaFee?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="tpaExpenses">TPA Expenses</Label>
                                <Controller name="tpaExpenses" control={control} render={({ field }) => (
                                    <Input id="tpaExpenses" type="number" {...field} invalid={!!errors.tpaExpenses} placeholder="Enter TPA expenses" />
                                )} />
                                <FormFeedback>{errors.tpaExpenses?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedRepair">Estimated Repair</Label>
                                <Controller name="estimatedRepair" control={control} render={({ field }) => (
                                    <Input id="estimatedRepair" type="number" {...field} invalid={!!errors.estimatedRepair} placeholder="Enter estimated repair cost" />
                                )} />
                                <FormFeedback>{errors.estimatedRepair?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedPd">Estimated Property Damage</Label>
                                <Controller name="estimatedPd" control={control} render={({ field }) => (
                                    <Input id="estimatedPd" type="number" {...field} invalid={!!errors.estimatedPd} placeholder="Enter estimated property damage amount" />
                                )} />
                                <FormFeedback>{errors.estimatedPd?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedLou">Estimated Loss of Use</Label>
                                <Controller name="estimatedLou" control={control} render={({ field }) => (
                                    <Input id="estimatedLou" type="number" {...field} invalid={!!errors.estimatedLou} placeholder="Enter estimated loss of use amount" />
                                )} />
                                <FormFeedback>{errors.estimatedLou?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedDv">Estimated Damage Vehicle</Label>
                                <Controller name="estimatedDv" control={control} render={({ field }) => (
                                    <Input id="estimatedDv" type="number" {...field} invalid={!!errors.estimatedDv} placeholder="Enter estimated diminished value" />
                                )} />
                                <FormFeedback>{errors.estimatedDv?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="estimatedTs">Estimated Tow & Storage</Label>
                                <Controller name="estimatedTs" control={control} render={({ field }) => (
                                    <Input id="estimatedTs" type="number" {...field} invalid={!!errors.estimatedTs} placeholder="Enter estimated tow/storage amount" />
                                )} />
                                <FormFeedback>{errors.estimatedTs?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredPd">Recovered Property Damage</Label>
                                <Controller name="recoveredPd" control={control} render={({ field }) => (
                                    <Input id="recoveredPd" type="number" {...field} invalid={!!errors.recoveredPd} placeholder="Enter recovered property damage amount" />
                                )} />
                                <FormFeedback>{errors.recoveredPd?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredLou">Recovered Loss of Use</Label>
                                <Controller name="recoveredLou" control={control} render={({ field }) => (
                                    <Input id="recoveredLou" type="number" {...field} invalid={!!errors.recoveredLou} placeholder="Enter recovered loss of use amount" />
                                )} />
                                <FormFeedback>{errors.recoveredLou?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredDv">Recovered Damage Vehicle</Label>
                                <Controller name="recoveredDv" control={control} render={({ field }) => (
                                    <Input id="recoveredDv" type="number" {...field} invalid={!!errors.recoveredDv} placeholder="Enter recovered diminished value" />
                                )} />
                                <FormFeedback>{errors.recoveredDv?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="recoveredTs">Recovered Tow & Storage</Label>
                                <Controller name="recoveredTs" control={control} render={({ field }) => (
                                    <Input id="recoveredTs" type="number" {...field} invalid={!!errors.recoveredTs} placeholder="Enter recovered tow/storage amount" />
                                )} />
                                <FormFeedback>{errors.recoveredTs?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="dailyRate">Daily Rate</Label>
                                <Controller name="dailyRate" control={control} render={({ field }) => (
                                    <Input id="dailyRate" type="number" {...field} invalid={!!errors.dailyRate} placeholder="Enter daily rate (if applicable)" />
                                )} />
                                <FormFeedback>{errors.dailyRate?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label for="daysDown">Days Down</Label>
                                <Controller name="daysDown" control={control} render={({ field }) => (
                                    <Input id="daysDown" type="number" {...field} invalid={!!errors.daysDown} placeholder="Enter number of days down" />
                                )} />
                                <FormFeedback>{errors.daysDown?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label for="notes">Notes</Label>
                                <Controller name="notes" control={control} render={({ field }) => (
                                    <Input id="notes" type="textarea" {...field} invalid={!!errors.notes} placeholder="Enter any additional notes or description" />
                                )} />
                                <FormFeedback>{errors.notes?.message}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    {error && <div className="text-danger small mb-2">{error}</div>}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeAndReset}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={loading}>Add</Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}

export default AddSubrogationClaimModal
